'use client'

import { useEffect, useState } from 'react'
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons'
import { mtc } from '../../../utils/style-helpers'
import { Input } from '../../atoms/input'
import { Popover } from '../../atoms/popover'
import { menuStyles, menuItemStyles } from './styles'

export interface AutocompleteProps {
  name: string
  error?: boolean
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onSelect?: (value: string) => void
  options: string[]
  placeholder?: string
  value?: string
  autoComplete?: string
}

export const Autocomplete = ({
  name,
  error,
  onBlur,
  onSelect,
  options,
  placeholder,
  value,
  autoComplete,
}: AutocompleteProps) => {
  const [query, setQuery] = useState(value ?? '')
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState<number | null>(null)

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(query.toLowerCase())
  )

  useEffect(() => {
    setActiveIdx(null)
  }, [query, options])

  const handleSelect = (option: string, idx: number) => {
    setQuery(option)
    setOpen(false)
    setActiveIdx(idx)
    onSelect?.(option)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || filteredOptions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIdx((prev) =>
          prev === null ? 0 : Math.min(prev + 1, filteredOptions.length - 1)
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIdx((prev) =>
          prev === null ? filteredOptions.length - 1 : Math.max(prev - 1, 0)
        )
        break
      case 'Enter':
        e.preventDefault()
        if (activeIdx !== null) {
          handleSelect(filteredOptions[activeIdx], activeIdx)
        }
        break
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        break
      default:
        break
    }
  }

  return (
    <Popover
      aria-controls={`${name}-dropdown-options`}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-owns={`${name}-dropdown-options`}
      defaultOpen={false}
      onOpenChange={setOpen}
    >
      <Popover.Trigger asChild>
        <Input
          aria-autocomplete="list"
          aria-expanded={open}
          aria-activedescendant={
            activeIdx !== null ? `option-${activeIdx}` : undefined
          }
          autoComplete={autoComplete}
          error={error}
          name={name}
          onBlur={onBlur}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          role="combobox"
          type="search"
          value={query}
          iconRight={faCaretDown}
          className="peer"
        />
      </Popover.Trigger>

      {open && filteredOptions.length > 0 && (
        <Popover.Content
          sideOffset={4}
          align="start"
          id={`${name}-dropdown-options`}
          role="listbox"
          className={mtc(menuStyles())}
          onOpenAutoFocus={(e) => e.preventDefault()}
          style={{ width: 'var(--radix-popover-trigger-width)' }}
        >
          {filteredOptions.map((option, idx) => (
            <div
              key={option}
              id={`option-${idx}`}
              role="option"
              tabIndex={-1}
              aria-selected={idx === activeIdx}
              className={mtc(menuItemStyles({ active: idx === activeIdx }))}
              onMouseDown={() => handleSelect(option, idx)}
            >
              {option}
            </div>
          ))}
        </Popover.Content>
      )}
    </Popover>
  )
}
